import {
  type AiResult,
  aiCanvasFor,
  aiFrameWidthFt,
  guidance,
  renderBatch,
  type TimePreset,
} from '@better-lights/core';
import { useAccount } from '../account/store.ts';
import { ApiError, isApiError } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import type { Render } from '../api/types.ts';
import { decodeRaw } from '../photo/import.ts';
import type { RenderService, RenderStart } from '../services/render.ts';
import { flushAutosave } from '../state/autosave.ts';
import { useDocument } from '../state/documentStore.ts';
import { useSettings } from '../state/settingsStore.ts';
import { blobUri } from '../storage/blobStore.ts';
import { db } from '../storage/db.ts';
import { storage } from '../storage/index.ts';
import { canSyncScope, type SyncServiceImpl } from '../sync/service.ts';
import { breadcrumb } from '../telemetry/sentry.ts';
import { buildAiImages } from './images.ts';
import { recordRenderMeta } from './meta.ts';
import { notifyRendersDone } from './notify.ts';
import { isTerminal, type RenderJobView, setJob, setRun, useRenders } from './store.ts';

const PENDING_KEY = 'renders.pending';

interface Pending {
  id: string;
  projectId: string;
  synced?: boolean;
}

/**
 * Unsynced projects (Free): the results are only returned on the render, so add them to the local
 * project as AiResults (the image blobs download lazily from the render's scope).
 */
export async function attachResults(r: Render, sync: SyncServiceImpl): Promise<void> {
  if (!r.results?.length) return;
  const results: AiResult[] = r.results.map((x) => ({
    id: x.aiResultId,
    time: r.time,
    uri: x.ref,
    width: x.width,
    height: x.height,
    createdAt: Date.parse(r.finishedAt ?? r.createdAt) || Date.now(),
    prompt: r.prompt ?? '',
    ...(r.look ? { look: r.look } : {}),
    // Same keys the server writes onto synced results (project.schema $defs.aiResult).
    ...(r.model ? { model: r.model } : {}),
    ...(r.promptVersion ? { promptVersion: r.promptVersion } : {}),
  }));
  for (const x of r.results) await sync.engine.ensureBlob(x.ref.replace(/^sha256:/, ''), r.scope);
  const doc = useDocument.getState();
  if (doc.projectId === r.projectId) {
    const fresh = results.filter((x) => !doc.aiResults.some((a) => a.id === x.id));
    if (fresh.length) doc.dispatch({ type: 'addAiResults', results: fresh });
    return;
  }
  const file = await storage().projects.get(r.projectId);
  if (!file) return;
  const fresh = results.filter((x) => !file.aiResults.some((a) => a.id === x.id));
  if (fresh.length)
    await storage().projects.save({ ...file, aiResults: [...fresh, ...file.aiResults], updatedAt: Date.now() });
}
const ORDER: TimePreset[] = ['day', 'evening', 'night'];

export class RemoteRenderService implements RenderService {
  private readonly aborts = new Map<string, AbortController>();
  /** Jobs whose results must be attached locally (project not synced). */
  private readonly unsynced = new Map<string, boolean>();

  constructor(private readonly sync: SyncServiceImpl) {}

  async start(req: RenderStart): Promise<void> {
    const acct = useAccount.getState();
    if (!acct.session || !acct.me) throw new ApiError(401, null, 'Sign in to create AI renders.');
    const times = ORDER.filter((t) => req.times.includes(t));
    setRun(req.projectId, {
      startedAt: Date.now(),
      total: times.length * Math.max(1, req.looks.length),
      phase: 'preparing',
      error: null,
      jobIds: [],
    });
    try {
      // 1. Synced workspaces: the project must be on the server (results are written into it).
      //    Without the `sync` feature (Free) the render runs in the scope and results come back on the render.
      await flushAutosave();
      let row = await db().projectSync.get(req.projectId);
      const scope = row?.scope ?? acct.activeScope ?? acct.me.personalScope;
      const synced = canSyncScope(scope);
      if (synced) {
        if (!row) {
          await this.sync.backup([req.projectId], scope);
          row = await db().projectSync.get(req.projectId);
        }
        await this.sync.syncNow();
        row = await db().projectSync.get(req.projectId);
        if (!row || row.baseRev === 0) throw new ApiError(0, null, 'Connect to the internet to create AI renders.');
      }
      const file = await storage().projects.get(req.projectId);
      if (!file) throw new Error('The project is missing.');

      // 2. Inputs at the AI canvas size, uploaded as blobs into the project's scope.
      const photoBlob = await storage().blobs.getByUri(file.photo.uri);
      if (!photoBlob) throw new Error('The photo is missing.');
      const bitmap = await decodeRaw(photoBlob);
      const settings = useSettings.getState();
      const canvas = aiCanvasFor(file.photo.width, file.photo.height, acct.me.ai.sizing);
      const doc = file.doc;
      const frameWidthFt = aiFrameWidthFt(doc.frameWidthFt, doc.runs, file.photo.width, file.photo.height);
      // One job per look × time (docs/spec/10); the guide / mask are per time and shared by looks.
      const batch = renderBatch(req.looks, times, { runs: doc.runs, style: doc.style, windowGlow: req.windowGlow });
      const prepared: { time: TimePreset; photo: string; guide: string | null; mask: string | null }[] = [];
      for (const time of times) {
        const imgs = await buildAiImages({
          photo: bitmap,
          canvas,
          time,
          runs: doc.runs,
          style: doc.style,
          colorId: doc.colorId,
          customHex: settings.customHex,
          frameWidthFt,
        });
        const photo = await storage().blobs.put(imgs.photo);
        const guide = imgs.guide ? await storage().blobs.put(imgs.guide) : null;
        const mask = imgs.mask ? await storage().blobs.put(imgs.mask) : null;
        prepared.push({ time, photo, guide, mask });
      }
      bitmap.close();
      const shas = [...new Set(prepared.flatMap((p) => [p.photo, p.guide, p.mask]).filter((s): s is string => !!s))];
      await this.sync.engine.uploadNow(scope, shas, 'render');

      // 3. One job per look × time, in parallel; one Idempotency-Key per job for retries.
      setRun(req.projectId, { phase: 'rendering', total: batch.count });
      const click = crypto.randomUUID();
      const jobs = await Promise.all(
        batch.renders.map(async ({ time, options }, i) => {
          const p = prepared.find((x) => x.time === time)!;
          const { hasGuide, hasMask } = guidance({ runs: doc.runs, time: p.time });
          const render = await api.createRender(
            {
              projectId: req.projectId,
              scope,
              time: p.time,
              count: 1,
              quality: req.quality,
              size: { width: canvas.width, height: canvas.height },
              photoSize: { width: file.photo.width, height: file.photo.height },
              photo: blobUri(p.photo),
              guide: hasGuide && p.guide ? blobUri(p.guide) : undefined,
              mask: hasMask && p.mask ? blobUri(p.mask) : undefined,
              options,
            },
            `render-${click}-${i}-${p.time}`,
          );
          return render;
        }),
      );
      setRun(req.projectId, { jobIds: jobs.map((j) => j.id) });
      await this.persistPending(jobs.map((j) => ({ id: j.id, projectId: j.projectId, synced })));
      for (const j of jobs) this.unsynced.set(j.id, !synced);
      for (const r of jobs) this.follow(r);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setRun(req.projectId, { phase: 'done', error: message });
      breadcrumb(
        'render',
        'Render could not start',
        { message, status: isApiError(e) ? e.status : undefined },
        'error',
      );
      throw e;
    }
  }

  async cancel(projectId: string): Promise<void> {
    const run = useRenders.getState().runs[projectId];
    if (!run) return;
    await Promise.all(
      run.jobIds.map(async (id) => {
        try {
          this.apply(await api.cancelRender(id));
        } catch {
          // already finished
        }
        this.aborts.get(id)?.abort();
      }),
    );
  }

  async resume(): Promise<void> {
    const pending = ((await db().kv.get(PENDING_KEY))?.value as Pending[] | undefined) ?? [];
    for (const p of pending) {
      this.unsynced.set(p.id, p.synced === false);
      try {
        const r = await api.getRender(p.id);
        setRun(r.projectId, {
          phase: 'rendering',
          jobIds: [...(useRenders.getState().runs[r.projectId]?.jobIds ?? []), r.id],
        });
        this.follow(r);
      } catch (e) {
        if (isApiError(e) && e.status === 404) await this.dropPending(p.id);
      }
    }
  }

  private follow(r: Render): void {
    this.apply(r);
    if (isTerminal(r.status)) return void this.finished(r);
    const ctrl = new AbortController();
    this.aborts.set(r.id, ctrl);
    let last = r;
    api
      .renderEvents(
        r.id,
        {
          render: (x) => {
            last = x;
            this.apply(x);
          },
          partial: (p) => setJob({ ...(useRenders.getState().jobs[r.id] as RenderJobView), partialUrl: p.url }),
        },
        ctrl.signal,
      )
      .catch(() => {})
      .then(() => (isTerminal(last.status) ? this.finished(last) : this.poll(r.id, ctrl.signal)));
  }

  /** Polling fallback (3 s, then 5 s; also when the stream ends early). */
  private async poll(id: string, signal: AbortSignal): Promise<void> {
    let wait = 3000;
    while (!signal.aborted) {
      await new Promise((res) => setTimeout(res, wait));
      wait = 5000;
      try {
        const r = await api.getRender(id, signal);
        this.apply(r);
        if (isTerminal(r.status)) return this.finished(r);
        wait = Math.max(1000, r.pollAfterMs || 5000);
      } catch {
        // offline: keep trying
      }
    }
  }

  private apply(r: Render): void {
    const prev = useRenders.getState().jobs[r.id];
    setJob({
      id: r.id,
      projectId: r.projectId,
      time: r.time,
      status: r.status,
      credits: r.credits,
      partialUrl: prev?.partialUrl,
      error: r.error?.message,
      createdAt: Date.parse(r.createdAt),
    });
  }

  private async finished(r: Render): Promise<void> {
    this.aborts.delete(r.id);
    await this.dropPending(r.id);
    const run = useRenders.getState().runs[r.projectId];
    const jobs = (run?.jobIds ?? [r.id]).map((id) => useRenders.getState().jobs[id]).filter(Boolean);
    if (r.status === 'succeeded') await recordRenderMeta(r).catch(() => {});
    if (r.status === 'succeeded' && this.unsynced.get(r.id)) await attachResults(r, this.sync);
    this.unsynced.delete(r.id);
    if (jobs.every((j) => isTerminal(j.status))) {
      const failed = jobs.filter((j) => j.status === 'failed');
      if (failed.length)
        breadcrumb(
          'render',
          'Render failed',
          { failed: failed.length, of: jobs.length, code: r.error?.code },
          'warning',
        );
      setRun(r.projectId, { phase: 'done', error: failed.length ? (failed[0].error ?? 'The render failed.') : null });
      const name = (
        await storage()
          .projects.get(r.projectId)
          .catch(() => null)
      )?.name;
      void notifyRendersDone({
        projectId: r.projectId,
        name,
        succeeded: jobs.filter((j) => j.status === 'succeeded').length,
        failed: failed.length,
      });
      await this.sync.syncNow(); // synced projects: results arrive through sync
    }
  }

  private async persistPending(items: Pending[]): Promise<void> {
    const cur = ((await db().kv.get(PENDING_KEY))?.value as Pending[] | undefined) ?? [];
    await db().kv.put({ key: PENDING_KEY, value: [...cur, ...items] });
  }

  private async dropPending(id: string): Promise<void> {
    const cur = ((await db().kv.get(PENDING_KEY))?.value as Pending[] | undefined) ?? [];
    await db().kv.put({ key: PENDING_KEY, value: cur.filter((p) => p.id !== id) });
  }
}
